import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { createElement } from 'react';
import { apiClient } from '../api/client';
import type { Locale } from '../i18n';
import { useAuthContext } from '../context/AuthContext';
import { THEME_DEFINITIONS, type ThemeName } from '@siliconvault/shared';

// --- Types ---

export type Theme = ThemeName;
export type DefaultView = 'grid' | 'table';
export type ItemsPerPage = 10 | 20 | 50;

export interface Preferences {
  theme: Theme;
  defaultView: DefaultView;
  itemsPerPage: ItemsPerPage;
  locale: Locale;
  showTicker: boolean;
  sortOrder: 'desc' | 'asc';
}

interface PreferencesContextValue {
  preferences: Preferences;
  setPreference: <K extends keyof Preferences>(key: K, value: Preferences[K]) => void;
  loadFromServer: () => Promise<void>;
  isLoaded: boolean;
}

// --- Defaults ---

const STORAGE_KEY = 'siliconvault-preferences';

const defaults: Preferences = {
  theme: 'modern_dark',
  defaultView: 'grid',
  itemsPerPage: 20,
  locale: 'en',
  showTicker: true,
  sortOrder: 'desc',
};

// --- Backward compat: map old theme names ---

function migrateThemeName(raw: string): Theme {
  if (raw === 'dark') return 'green_phosphor';
  if (raw === 'light') return 'amber_phosphor';
  return raw as Theme;
}

// --- Locale / direction helpers ---

const RTL_LOCALES = new Set<Locale>(['he']);

function applyLocale(locale: Locale) {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = RTL_LOCALES.has(locale) ? 'rtl' : 'ltr';
}

// --- Theme variable map ---

const ALL_THEME_PROPS = Object.keys(
  THEME_DEFINITIONS.modern_dark.colors,
) as Array<keyof typeof THEME_DEFINITIONS.modern_dark.colors>;

function getThemeVars(theme: Theme): Record<string, string> {
  const def = THEME_DEFINITIONS[theme];
  const colors = def ? def.colors : THEME_DEFINITIONS.modern_dark.colors;
  return { ...colors };
}

// --- Theme application ---

/** When true, the PreferencesProvider skips automatic theme application. */
let themeOverrideActive = false;
export function setThemeOverride(active: boolean) { themeOverrideActive = active; }

export function applyTheme(themeId: Theme) {
  const root = document.documentElement;
  if (themeId === 'modern_dark') {
    // Modern dark is the :root baseline — remove all inline overrides
    for (const prop of ALL_THEME_PROPS) {
      root.style.removeProperty(prop);
    }
  } else {
    const vars = getThemeVars(themeId);
    for (const [prop, value] of Object.entries(vars)) {
      root.style.setProperty(prop, value);
    }
  }
  root.dataset.theme = themeId;
}

function applyPreferences(prefs: Preferences) {
  applyTheme(prefs.theme);
  applyLocale(prefs.locale);
}

// --- Local cache (for instant theme on page load) ---

function loadCachedPreferences(): Preferences {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed.theme) {
        parsed.theme = migrateThemeName(parsed.theme);
      }
      return { ...defaults, ...parsed };
    }
  } catch {
    // ignore corrupt data
  }
  return defaults;
}

function cachePreferences(prefs: Preferences) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
}

// --- Context ---

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [preferences, setPreferences] = useState<Preferences>(() => {
    const prefs = loadCachedPreferences();
    applyPreferences(prefs);
    return prefs;
  });
  const [isLoaded, setIsLoaded] = useState(false);
  const syncTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadFromServer = useCallback(async () => {
    try {
      const serverPrefs = await apiClient.getPreferences();
      const rawTheme = typeof serverPrefs.theme === 'string' ? serverPrefs.theme : '';
      const merged: Preferences = {
        theme: migrateThemeName(rawTheme) || defaults.theme,
        defaultView: (serverPrefs.defaultView as DefaultView) || defaults.defaultView,
        itemsPerPage: (serverPrefs.itemsPerPage as ItemsPerPage) || defaults.itemsPerPage,
        locale: (serverPrefs.locale as Locale) || defaults.locale,
        showTicker: typeof serverPrefs.showTicker === 'boolean' ? serverPrefs.showTicker : defaults.showTicker,
        sortOrder: ['desc', 'asc'].includes((serverPrefs as any).sortOrder) ? (serverPrefs as any).sortOrder : defaults.sortOrder,
      };
      setPreferences(merged);
      cachePreferences(merged);
      applyPreferences(merged);
    } catch {
      // Not authenticated or server down — keep cached/defaults
    } finally {
      setIsLoaded(true);
    }
  }, []);

  const { onLogin, isAuthenticated } = useAuthContext();

  // Load from server when authenticated (on mount or after login)
  useEffect(() => {
    if (isAuthenticated) {
      loadFromServer();
    }
  }, [isAuthenticated, loadFromServer]);

  // Re-load after login/register
  useEffect(() => {
    return onLogin(() => { loadFromServer(); });
  }, [onLogin, loadFromServer]);

  // Apply theme + locale whenever preferences change
  useEffect(() => {
    if (!themeOverrideActive) applyTheme(preferences.theme);
    applyLocale(preferences.locale);
    cachePreferences(preferences);
  }, [preferences]);

  const setPreference = useCallback(
    <K extends keyof Preferences>(key: K, value: Preferences[K]) => {
      setPreferences((prev) => {
        const next = { ...prev, [key]: value };
        cachePreferences(next);
        if (!themeOverrideActive) applyTheme(next.theme);
        applyLocale(next.locale);

        // Debounce server sync (300ms)
        if (syncTimer.current) clearTimeout(syncTimer.current);
        syncTimer.current = setTimeout(() => {
          apiClient.updatePreferences({ [key]: value }).catch(() => {
            // Silently fail — local cache is source of truth for UX
          });
        }, 300);

        return next;
      });
    },
    [],
  );

  const value = useMemo(
    () => ({ preferences, setPreference, loadFromServer, isLoaded }),
    [preferences, setPreference, loadFromServer, isLoaded],
  );

  return createElement(
    PreferencesContext.Provider,
    { value },
    children,
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (!context) {
    throw new Error('usePreferences must be used within a PreferencesProvider');
  }
  return context;
}
